"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight } from "lucide-react";
import { clsx } from "clsx";
import { Mascot } from "@/components/Mascot";

const STORAGE_KEY = "uniui_guest_welcome_seen";

/**
 * GuestMascotGreeter — first-entry guidance for guest (unauthenticated)
 * visitors. Shows a dismissible welcome card with directional cues pointing
 * new users toward Ask AI. Gated by localStorage so it appears once.
 */
export function GuestMascotGreeter({
  navCollapsed = false,
}: {
  navCollapsed?: boolean;
}) {
  const [mounted, setMounted] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    setMounted(true);
    try {
      if (!localStorage.getItem(STORAGE_KEY)) setShow(true);
    } catch {
      /* storage unavailable — stay hidden */
    }
  }, []);

  const dismiss = () => {
    setShow(false);
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  if (!mounted || !show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 24, scale: 0.96 }}
        transition={{ type: "spring", bounce: 0.25, duration: 0.4 }}
        className={clsx(
          // pointer-events-none on the fixed layer: the greeter is a global
          // overlay rendered by LayoutWrapper, and at z-150 it sat on top of
          // the answer card, covering the "Common misconception" section and
          // swallowing clicks. The panel re-enables pointer events for itself.
          "pointer-events-none fixed bottom-24 left-3 z-[150] lg:bottom-6 lg:left-4 w-[min(19rem,calc(100vw-2rem))]",
          navCollapsed ? "xl:left-3" : "xl:left-3",
        )}
        role="status"
        aria-label="Welcome to UniUI"
      >
        <div className="pointer-events-auto matte-metal rounded-2xl border border-y/40 p-4 shadow-2xl flex gap-3">
          <span className="relative shrink-0 inline-flex">
            <span
              className="absolute inset-0 bg-y/30 animate-pulse-gold"
              aria-hidden="true"
            />
            <Mascot state="idle" size="medium" className="relative" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-black text-foreground leading-tight">
              Hey, I&apos;m Uni.
            </p>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              New here? Ask me anything — your first question is free.
            </p>
            <p className="text-xs font-bold uppercase tracking-wider text-y mt-1.5">
              Press ⌘K to jump anywhere
            </p>
            <div className="flex items-center gap-2 mt-3">
              <Link
                href="/ask"
                onClick={dismiss}
                className="inline-flex items-center gap-1 rounded-full bg-foreground text-background px-3 py-1.5 text-xs font-black uppercase tracking-wider hover:opacity-90 transition-opacity"
              >
                Ask AI
                <ArrowRight size={13} />
              </Link>
              <button
                onClick={dismiss}
                className="text-xs font-bold text-muted-foreground underline-offset-2 hover:underline"
              >
                Dismiss
              </button>
            </div>
          </div>
          <button
            onClick={dismiss}
            aria-label="Dismiss welcome"
            className="absolute top-2 right-2 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
          >
            <X size={14} />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
